import { useState } from "react";
import { Button } from "@/client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { Notice } from "@/client/components/ui/notice";
import { AlertCircleIcon, RotateCcwIcon } from "lucide-react";
import { formatDutchDate } from "@/client/lib/portal";

type Flow = "schoolgegevens" | "programmaselectie";

/** A calm confirm dialog for "Wijzigingen terugzetten": it puts the school's live data back to
 *  its last aanlevering, for one section or the whole flow. The server endpoint (POST /revert)
 *  accepts a single sectionCode, so a UI section that spans more than one server section (the
 *  Vast- & keuzeprogramma card maps to fixed_programme + optional_programme) passes several
 *  codes and we revert them in order. `sectionCodes` omitted = the whole flow.
 *
 *  On confirm it reverts, then calls onReverted() so the page can invalidate + refresh its state
 *  (the reverted section/flow flips back to "Afgerond"/green). Shows a brief saving + error state. */
export function RevertChangesDialog({
  open,
  onOpenChange,
  schoolId,
  flow,
  sectionCodes,
  lastSubmittedAt,
  scope,
  onReverted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: number;
  flow: Flow;
  /** Server section codes to revert. Omitted/empty = revert the whole flow. */
  sectionCodes?: string[];
  /** When this flow was last aangeleverd, for the "naar wat u het laatst heeft aangeleverd op {datum}" line. */
  lastSubmittedAt: string | null;
  /** "section" tweaks the copy to "deze sectie", "flow" to "al uw wijzigingen". */
  scope: "section" | "flow";
  /** Called after a successful revert so the page can invalidate + refresh its state. */
  onReverted: () => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const datum = formatDutchDate(lastSubmittedAt);
  const wat = scope === "section" ? "deze sectie" : "al uw wijzigingen";

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(false);
    try {
      // Whole-flow: one call without sectionCode. Per-section: one call per code (the Vast- &
      // keuzeprogramma card spans two server sections). Any failure aborts and shows the error.
      const codes = sectionCodes && sectionCodes.length > 0 ? sectionCodes : [undefined];
      for (const sectionCode of codes) {
        const res = await fetch(`/api/school/${schoolId}/revert`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(sectionCode ? { flow, sectionCode } : { flow }),
        });
        if (!res.ok) {
          setError(true);
          setSubmitting(false);
          return;
        }
      }
      await onReverted();
      setSubmitting(false);
      onOpenChange(false);
    } catch {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) { setError(false); onOpenChange(false); } }}>
      <DialogContent className="max-w-md" closeProps={{ disabled: submitting }}>
        <DialogHeader>
          <DialogTitle>Wijzigingen terugzetten?</DialogTitle>
          <DialogDescription>
            Hiermee zet u {wat} terug naar wat u
            {datum ? ` het laatst heeft aangeleverd op ${datum}` : " het laatst heeft aangeleverd"}.
            Wijzigingen die u sindsdien heeft gemaakt en nog niet opnieuw heeft aangeleverd, gaan
            verloren.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="px-6">
            <Notice tone="error" icon={<AlertCircleIcon />} title="Het terugzetten is niet gelukt">
              Controleer uw internetverbinding en probeer het opnieuw.
            </Notice>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { setError(false); onOpenChange(false); }}
            disabled={submitting}
          >
            Annuleren
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            <RotateCcwIcon aria-hidden size={16} className="-ms-0.5 opacity-80" />
            {submitting ? "Bezig met terugzetten..." : "Ja, terugzetten"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
